/**
 * LambdaPlaceholderService
 *
 * Integrates Lambda-generated placeholders from S3 with the newsletter system
 * Fetches AI-generated content from scheduled Lambda executions
 */

import logger from '../utils/logger.js';
import customContentService from './CustomContentService.js';

/**
 * #367 — portfolio-family placeholders the MVP must NEVER render.
 * These require real client portfolio data (CLAUDE.md "MVP Limitations");
 * without it they produce fabricated returns or broken-looking filler in a
 * client-facing letter. fillTemplate/fillTemplateAsync strip them from
 * output UNCONDITIONALLY — even if a caller supplies values — until real
 * portfolio integration ships.
 */
export const BANNED_PORTFOLIO_PLACEHOLDERS = new Set([
    'monthly_performance_summary',
    'monthly_return',
    'ytd_return',
    'sharpe_ratio',
    'allocation_summary',
    'performance_attribution',
    'top_performers',
    'bottom_performers',
    'rebalancing_suggestions',
    'tax_optimization',
    'investment_insights.rebalancing_suggestions',
]);

// Browser-compatible version - uses fetch API instead of AWS SDK
class LambdaPlaceholderService {
    constructor() {
        // Create service-specific logger
        this.logger = logger.child('LambdaPlaceholderService');

        this.bucketName = import.meta.env?.VITE_S3_BUCKET || 'financial-newsletter-data';
        this.region = import.meta.env?.VITE_AWS_REGION || 'us-west-2';
        this.cache = new Map();
        this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
        this.maxCacheSize = 20;
        // In browser environment, we'll use the API endpoint instead of direct S3 access
        this.apiEndpoint = import.meta.env?.VITE_API_ENDPOINT || '/api';

        // #363: placeholders come from S3 only (placeholders/latest.json); the
        // batch-placeholder-generator Function URL is IAM-only and never called here.
        this.logger.info('Initialized with:', { apiEndpoint: this.apiEndpoint });
    }

    /**
     * Fetch latest placeholders from S3 based on schedule type with market context
     * @param {string} scheduleType - pre-market, mid-day, post-market, evening
     * @param {Object} marketData - Market context from MarketContextService (optional - will fetch if not provided)
     * @returns {Object} Placeholder data
     */
    async fetchLatestPlaceholders(scheduleType = 'latest', marketData = null) {
        const cacheKey = `placeholders-${scheduleType}`;

        // Check cache
        if (this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheExpiry) {
                this.logger.info(`Returning cached placeholders for ${scheduleType}`);
                return cached.data;
            }
        }

        try {
            // NOTE: Market context is NOT needed for loading pre-generated placeholders from S3.
            // The scheduled Lambda batch job already generated placeholders with full market context.
            // We simply fetch and return the pre-generated data.

            let data;

            // PRIMARY: Fetch from S3 (pre-generated by scheduled Lambda batch job)
            // Lambda Function URL is NOT used for synchronous API calls (takes 84+ seconds to execute)
            // Architecture: Scheduled Lambda → S3 → Frontend
            this.logger.info(`[PRIMARY] Fetching placeholders from S3 for ${scheduleType}...`);

            // Step 1: Fetch pointer file to get actual data file path
            const pointerUrl = `https://${this.bucketName}.s3.${this.region}.amazonaws.com/placeholders/latest.json`;

            try {
                this.logger.info(`Fetching S3 pointer file: ${pointerUrl}`);
                const pointerResponse = await fetch(pointerUrl);

                if (!pointerResponse.ok) {
                    throw new Error(`S3 pointer file fetch failed: ${pointerResponse.status}`);
                }

                const pointerData = await pointerResponse.json();
                this.logger.info(`Pointer file retrieved:`, pointerData);

                // Step 2: Fetch actual data file using path from pointer
                const actualDataPath = pointerData.latestBatch;
                if (!actualDataPath) {
                    throw new Error('S3 pointer file missing latestBatch field');
                }

                const actualDataUrl = `https://${this.bucketName}.s3.${this.region}.amazonaws.com/${actualDataPath}`;
                this.logger.info(`Fetching actual data file: ${actualDataUrl}`);

                const dataResponse = await fetch(actualDataUrl);

                if (!dataResponse.ok) {
                    throw new Error(`S3 data file fetch failed: ${dataResponse.status}`);
                }

                data = await dataResponse.json();
                data.isFallback = false; // Real data from S3
                data.isProductionReady = true; // Real AI data from S3 - safe for production
                this.logger.info('✅ Successfully fetched from S3');
            } catch (s3Error) {
                this.logger.warn('S3 primary fetch failed:', s3Error.message);

                // FALLBACK: Try manual schedule directly
                this.logger.info('[FALLBACK] Trying manual placeholder file...');
                try {
                    const manualUrl = `https://${this.bucketName}.s3.${this.region}.amazonaws.com/placeholders/manual/latest.json`;
                    const manualResponse = await fetch(manualUrl);

                    if (manualResponse.ok) {
                        data = await manualResponse.json();
                        this.logger.warn('⚠️  Using fallback manual placeholders');
                        data.isFallback = true;
                        data.isProductionReady = false;
                        data.fallbackType = 'manual';
                        data.fallbackReason = 'Primary S3 pointer unavailable, using direct manual file access';
                        data.warningMessage = 'Using fallback placeholders. Latest data temporarily unavailable. Do not send newsletters with this data.';
                    } else {
                        throw new Error(`Manual schedule fetch failed: ${manualResponse.status}`);
                    }
                } catch (manualError) {
                    this.logger.error('All S3 sources failed:', manualError.message);

                    // LAST RESORT: Return error state with empty placeholders
                    this.logger.error('All placeholder sources unavailable');
                    return {
                        error: true,
                        isFallback: true,
                        isProductionReady: false,
                        fallbackType: 'error',
                        fallbackReason: 'All S3 placeholder sources unavailable',
                        message: 'Placeholder content temporarily unavailable',
                        warningMessage: 'All data sources failed. Cannot load real-time placeholder content. Displaying example/mock data for demonstration purposes only. Do not send newsletters at this time.',
                        timestamp: new Date().toISOString(),
                        scheduleType: scheduleType,
                        placeholders: {} // Empty placeholders object
                    };
                }
            }

            // Cache the result (with size limit to prevent memory leaks)
            if (this.cache.size >= this.maxCacheSize) {
                const oldestKey = this.cache.keys().next().value;
                this.cache.delete(oldestKey);
            }
            this.cache.set(cacheKey, {
                timestamp: Date.now(),
                data: data
            });

            return data;
        } catch (error) {
            this.logger.error('Unexpected error in fetchLatestPlaceholders:', error);

            // Return error state
            return {
                error: true,
                isFallback: true,
                isProductionReady: false,
                fallbackType: 'error',
                fallbackReason: error.message || 'Unexpected error while fetching placeholders',
                message: 'An error occurred while loading placeholder data. Please try again later.',
                warningMessage: 'All data sources failed. Cannot load real-time placeholder content. Displaying example/mock data for demonstration purposes only. Do not send newsletters at this time.',
                timestamp: new Date().toISOString(),
                scheduleType,
                placeholders: {}
            };
        }
    }

    /**
     * Get placeholders for current market schedule
     * @returns {Object} Current placeholders based on time
     */
    async getCurrentPlaceholders() {
        const scheduleType = this.getCurrentScheduleType();
        return await this.fetchLatestPlaceholders(scheduleType);
    }

    /**
     * Determine current schedule type based on time
     * @returns {string} Schedule type
     */
    getCurrentScheduleType() {
        const now = new Date();
        const hour = now.getHours();
        const day = now.getDay();

        // Weekend
        if (day === 0 || day === 6) {
            return 'evening'; // Use evening content for weekends
        }

        // Weekday schedule (EST)
        if (hour < 7) {
            return 'evening'; // Use previous evening's content
        } else if (hour < 12) {
            return 'pre-market';
        } else if (hour < 16) {
            return 'mid-day';
        } else if (hour < 20) {
            return 'post-market';
        } else {
            return 'evening';
        }
    }

    /**
     * Transform Lambda placeholders to newsletter format
     * @param {Object} lambdaData - Raw Lambda placeholder data
     * @returns {Object} Formatted placeholders
     */
    transformPlaceholders(lambdaData) {
        if (!lambdaData || !lambdaData.placeholders) {
            return {};
        }

        const transformed = {};
        const placeholders = lambdaData.placeholders;

        // Map Lambda placeholders to newsletter placeholders
        Object.keys(placeholders).forEach(key => {
            // Convert snake_case to camelCase if needed
            const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
            transformed[camelKey] = placeholders[key];
        });

        // Add metadata
        transformed._metadata = {
            generatedAt: lambdaData.timestamp,
            scheduleType: lambdaData.scheduleType,
            source: 'lambda-batch-generator'
        };

        return transformed;
    }

    /**
     * Get specific placeholder value
     * @param {string} placeholderName - Name of the placeholder
     * @returns {string} Placeholder value
     */
    async getPlaceholder(placeholderName) {
        const placeholders = await this.getCurrentPlaceholders();
        const transformed = this.transformPlaceholders(placeholders);
        return transformed[placeholderName] || this.getDefaultValue(placeholderName);
    }

    /**
     * Get multiple placeholders
     * @param {Array} placeholderNames - Array of placeholder names
     * @returns {Object} Map of placeholder values
     */
    async getMultiplePlaceholders(placeholderNames) {
        const placeholders = await this.getCurrentPlaceholders();
        const transformed = this.transformPlaceholders(placeholders);
        
        const result = {};
        placeholderNames.forEach(name => {
            result[name] = transformed[name] || this.getDefaultValue(name);
        });
        
        return result;
    }

    /**
     * Generate appropriate prompt for different schedule types with market context
     * @param {string} scheduleType - Schedule type
     * @param {Object} marketContext - Market context data
     * @returns {string} Prompt for Lambda
     */
    generatePromptForSchedule(scheduleType, marketContext = {}) {
        // Build market context section for the prompt
        const contextSection = marketContext.combinedNarrative
            ? `\n\nMARKET CONTEXT:\n${marketContext.combinedNarrative}\n\nCurrent Market Data:\n${JSON.stringify(marketContext.indices || {}, null, 2)}\nVIX: ${marketContext.vix || 'N/A'}\n`
            : '';

        const prompts = {
            'pre-market': `Generate pre-market financial newsletter content including:
- Market summary for Asian/European markets overnight
- Key economic indicators released today
- Top market movers and sector trends
- Pre-market futures outlook
- Key risks and opportunities for the trading day
${contextSection}
Format as JSON with keys: market_summary, portfolio_update, key_insight, action_item, market_outlook, top_mover, economic_highlight, risk_alert, opportunity, weekly_theme`,

            'mid-day': `Generate mid-day financial newsletter content including:
- Morning trading session recap
- Intraday market trends and momentum
- Sector rotation and leadership
- Key stock movements and catalysts
- Economic data impact analysis
${contextSection}
Format as JSON with keys: market_summary, portfolio_update, key_insight, action_item, market_outlook, top_mover, economic_highlight, risk_alert, opportunity, weekly_theme`,

            'post-market': `Generate post-market financial newsletter content including:
- Full trading day summary and analysis
- Market performance across major indices
- Significant stock movements and drivers
- Economic data interpretation
- After-hours activity
${contextSection}
Format as JSON with keys: market_summary, portfolio_update, key_insight, action_item, market_outlook, top_mover, economic_highlight, risk_alert, opportunity, weekly_theme`,

            'evening': `Generate evening financial newsletter content including:
- Full day market wrap-up
- Sector performance analysis
- Key takeaways and themes
- Preparation for next trading day
- Strategic insights and opportunities
${contextSection}
Format as JSON with keys: market_summary, portfolio_update, key_insight, action_item, market_outlook, top_mover, economic_highlight, risk_alert, opportunity, weekly_theme`,

            'latest': `Generate financial newsletter content including:
- Current market overview and sentiment
- Portfolio performance update
- Key insights and analysis
- Actionable recommendations
- Market outlook
${contextSection}
Format as JSON with keys: market_summary, portfolio_update, key_insight, action_item, market_outlook, top_mover, economic_highlight, risk_alert, opportunity, weekly_theme`
        };

        return prompts[scheduleType] || prompts['latest'];
    }

    /**
     * Parse Lambda response into expected placeholder format
     * @param {Object} lambdaResponse - Response from Lambda
     * @param {string} scheduleType - Schedule type
     * @returns {Object} Parsed placeholders
     */
    parseLambdaResponse(lambdaResponse, scheduleType) {
        try {
            // Lambda returns { content: "..." } where content is the AI-generated text
            const content = lambdaResponse.content || lambdaResponse.text || '';

            // Try to parse as JSON if it looks like JSON
            if (content.trim().startsWith('{')) {
                try {
                    return JSON.parse(content);
                } catch (e) {
                    this.logger.warn('Lambda response is not valid JSON, using text extraction');
                }
            }

            // Extract key-value pairs from text using pattern matching
            const placeholders = {};
            const lines = content.split('\n');

            // Look for patterns like "key: value" or "key - value"
            lines.forEach(line => {
                const match = line.match(/^[\s-]*([a-z_]+)[\s:=-]+(.+)$/i);
                if (match) {
                    const key = match[1].trim().toLowerCase().replace(/\s+/g, '_');
                    const value = match[2].trim();
                    placeholders[key] = value;
                }
            });

            // If we extracted some data, return it
            if (Object.keys(placeholders).length > 0) {
                return placeholders;
            }

            // Otherwise, use the entire content as market_summary
            return {
                market_summary: content || 'Market analysis is being generated.',
                portfolio_update: 'Your portfolio update is being prepared.',
                key_insight: 'Key insights will be available shortly.',
                action_item: 'Review your investment strategy.',
                market_outlook: 'Market outlook is being analyzed.',
                top_mover: 'Analyzing top market movers.',
                economic_highlight: 'Economic highlights being compiled.',
                risk_alert: 'Risk assessment in progress.',
                opportunity: 'Identifying investment opportunities.',
                weekly_theme: `${scheduleType} market update`
            };
        } catch (error) {
            this.logger.error('Error parsing Lambda response:', error);
            return this.getDefaultPlaceholders().placeholders;
        }
    }

    /**
     * Get default placeholders when Lambda data is unavailable
     * @returns {Object} Default placeholder data
     */
    getDefaultPlaceholders() {
        return {
            timestamp: new Date().toISOString(),
            scheduleType: 'default',
            isFallback: true,
            fallbackType: 'hardcoded-defaults',
            placeholders: {
                market_summary: 'Markets showed mixed results today as investors digest latest economic data.',
                portfolio_update: 'Your portfolio remains well-positioned for current market conditions.',
                key_insight: 'Focus on long-term fundamentals amid short-term volatility.',
                action_item: 'Review your asset allocation to ensure alignment with goals.',
                market_outlook: 'Markets expected to remain range-bound pending key economic releases.',
                top_mover: 'Technology sector leading gains on positive earnings outlook.',
                economic_highlight: 'Economic indicators suggest steady growth momentum.',
                risk_alert: 'Monitor interest rate expectations and their impact on valuations.',
                opportunity: 'Consider selective opportunities in quality growth stocks.',
                weekly_theme: 'Earnings season continues to drive individual stock movements.'
            }
        };
    }

    /**
     * Get default value for a specific placeholder
     * @param {string} placeholderName - Name of the placeholder
     * @returns {string} Default value
     */
    getDefaultValue(placeholderName) {
        const defaults = {
            marketSummary: 'Market conditions remain stable.',
            portfolioUpdate: 'Portfolio performing as expected.',
            keyInsight: 'Stay focused on long-term goals.',
            actionItem: 'Review your investment strategy.',
            marketOutlook: 'Markets expected to remain steady.',
            topMover: 'Sector rotation continues.',
            economicHighlight: 'Economic data within expectations.',
            riskAlert: 'Monitor market developments.',
            opportunity: 'Selective opportunities available.',
            weeklyTheme: 'Market dynamics evolving.'
        };
        
        return defaults[placeholderName] || `[${placeholderName}]`;
    }

    /**
     * Clear cache to force fresh fetch
     */
    clearCache() {
        this.cache.clear();
        this.logger.info('Lambda placeholder cache cleared');
    }

    /**
     * Test API connection
     * @returns {boolean} Connection status
     */
    async testConnection() {
        try {
            const response = await fetch(`${this.apiEndpoint}/health`);
            this.logger.info(`API connection test: ${response.ok ? 'successful' : 'failed'}`);
            return response.ok;
        } catch (error) {
            this.logger.error(`Failed to connect to API: ${this.apiEndpoint}`, error);
            return false;
        }
    }

    /**
     * Get non-client-specific placeholders that can be AI-generated
     * These placeholders don't require client portfolio data and can be provided generically
     * @returns {Object} 27 non-client-specific placeholders with AI-generated content
     */
    getNonClientPlaceholders() {
        const now = new Date();
        const quarter = Math.floor(now.getMonth() / 3) + 1;
        const year = now.getFullYear();

        return {
            // System Configuration (10)
            'portal_link': 'https://client-portal.finnewsy.com - Access your secure client portal 24/7 for real-time portfolio monitoring, documents, and account management.',
            'app_link': 'Download our mobile app for iOS and Android at https://finnewsy.com/mobile-app to stay connected with your portfolio on-the-go.',
            'support_contact': 'For immediate assistance, contact our dedicated support team at support@finnewsy.com or call 1-800-FIN-NEWS (346-6397). Our advisors are available Monday-Friday, 8 AM - 6 PM EST.',
            'update_frequency': 'You will receive comprehensive portfolio updates weekly, with additional market alerts during significant events. Quarterly reviews include in-depth performance analysis and strategic outlook.',
            'review_schedule': 'Your quarterly portfolio reviews are scheduled for the first week of January, April, July, and October. We will send calendar invites 2 weeks in advance.',
            'commentary_schedule': 'Market commentary is published every Monday and Thursday morning, with special updates during major market events or economic announcements.',
            'tax_document_schedule': 'Tax documents are available as follows: 1099s by February 15th, K-1s by March 31st, year-end summaries by January 31st. All documents are accessible in your client portal.',
            'communication_setup': 'Customize your communication preferences in the client portal under Settings > Communications. Choose email frequency, alert thresholds, and preferred contact methods.',
            'newsletter_date': now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
            'quarter_number': `Q${quarter}`,

            // Strategic Content (9)
            'strategic_adjustments': 'Based on current market conditions, we recommend rebalancing from growth to value sectors, increasing fixed income allocation by 5%, and maintaining cash reserves at 10% for opportunistic investments.',
            'quarterly_action_items': '• Review and rebalance portfolio allocations\n• Harvest tax losses in underperforming positions\n• Maximize retirement contributions before year-end\n• Schedule estate planning review\n• Update beneficiary designations',
            'income_strategy': 'Our income generation strategy focuses on dividend aristocrats yielding 3-4%, investment-grade corporate bonds, and selective REIT exposure. Target annual income: 4.5% of portfolio value.',
            'healthcare_planning': 'Healthcare planning considerations: Review Medicare enrollment windows, evaluate supplemental insurance needs, maximize HSA contributions ($4,150 individual/$8,300 family for 2025), and plan for long-term care expenses.',
            'retirement_action_items': '• Confirm 401(k) contributions are maximized\n• Review Roth conversion opportunities\n• Update retirement income projections\n• Evaluate Social Security claiming strategies\n• Assess healthcare coverage gaps',
            'advisor_response': 'Our investment committee views recent market volatility as a normal correction within a broader uptrend. We maintain our constructive outlook while remaining vigilant for opportunities to enhance portfolio positioning.',
            'immediate_recommendations': 'Immediate action recommended: 1) Increase international equity exposure by 3%, 2) Add duration to bond portfolio, 3) Establish positions in undervalued sectors, 4) Review and update risk management stops.',
            'onboarding_overview': 'Your onboarding journey includes: Initial consultation, risk assessment, investment policy statement development, account setup, initial funding, portfolio construction, and quarterly review scheduling - typically completed within 2-3 weeks.',
            'onboarding_checklist': '✓ Complete risk assessment questionnaire\n✓ Provide financial statements\n✓ Sign investment advisory agreement\n✓ Fund initial account deposit\n✓ Set up online portal access\n✓ Schedule first quarterly review\n✓ Download mobile app',

            // Educational Content (8)
            'investment_philosophy': 'Our investment philosophy centers on evidence-based portfolio construction, strategic asset allocation, disciplined rebalancing, tax-efficient implementation, and long-term wealth preservation. We prioritize time in market over market timing.',
            '401k_limit': `401(k) contribution limits are set annually by the IRS, with a higher catch-up allowance from age 50. Confirm the current year's limits at IRS.gov or with your advisor before contributing.`,
            'ira_limit': `IRA contribution limits and Roth IRA income phase-outs are set annually by the IRS, with a higher catch-up allowance from age 50. Confirm the current year's figures at IRS.gov or with your advisor.`,
            'catchup_limits': 'Catch-up contribution limits for 2025 (age 50+): 401(k) additional $7,500, IRA additional $1,000, SIMPLE IRA additional $3,500, HSA additional $1,000. New: Super catch-up for ages 60-63 in 401(k) plans.',
            'deduction_strategies': 'Key tax deduction strategies: Maximize retirement contributions, bunch charitable donations, harvest investment losses, contribute to HSAs, time medical expenses, utilize qualified business deductions, and consider donor-advised funds.',
            'charitable_strategies': 'Optimize charitable giving through: Donor-advised funds for multi-year deductions, qualified charitable distributions from IRAs (age 70½+), donation of appreciated securities, charitable remainder trusts, and strategic bunching in high-income years.',
            'tax_efficient_investments': 'Tax-efficient investment vehicles: Municipal bonds (for high earners), index funds (low turnover), ETFs (tax-loss harvesting), qualified dividends, tax-managed funds, I Bonds (state tax-exempt), and master limited partnerships in IRAs.',
            'welcome_support': 'Welcome to Finnewsy! Your dedicated support team is ready to assist you. Access resources at finnewsy.com/welcome, join our new client webinar series, or schedule a one-on-one orientation session with your advisor.'
        };
    }

    /**
     * DEPRECATED alias (#367 / Rule 17): the name "getAllPlaceholders" caused
     * repeated confusion with fetchLatestPlaceholders (real S3 data) — the
     * Dec 31, 2025 bug shipped static samples to previews because of it.
     * Use getStaticFallbackPlaceholders() so the mock nature is explicit.
     */
    getAllPlaceholders() {
        return this.getStaticFallbackPlaceholders();
    }

    /**
     * Get the STATIC FALLBACK placeholder catalog (synchronous) for preview
     * rendering when S3 data is unavailable.
     * ⚠️ WARNING: Returns HARDCODED mock/example data - NOT production-ready.
     * Values must never assert specific dollar figures, rates, or returns —
     * IRS limits and Fed policy change, and a leaked figure in a sent letter
     * is a compliance incident (#367).
     * @returns {Object} All placeholder keys with sample values + production safety flags
     */
    getStaticFallbackPlaceholders() {
        const placeholders = {
            // Market Overview
            'market_overview.summary': 'US markets posted mixed results with the S&P 500 edging down -0.13% as gains in mid-caps offset weakness in large-cap technology.',
            'market_overview.performance': 'S&P 500: -0.13%, Dow Jones: -0.27%, NASDAQ: -0.07%, S&P 400 Mid Cap: +0.09%',
            'market_overview.outlook': 'Markets expected to remain range-bound as investors await Fed policy clarity and Q4 earnings reports.',
            'market_overview.keyDrivers': 'Key drivers: Fed policy expectations, strong consumer spending, sectoral rotation, geopolitical developments.',
            'market_overview.volatility': 'Market volatility remains subdued with VIX at 18.45, indicating low investor fear.',
            'market_overview.volume': 'Trading volume reached 3.2 billion shares, above 30-day average.',
            'market_overview.breadth': 'Market breadth positive with 1,800 advancing vs 1,200 declining stocks.',
            'market_overview.sentiment': 'Sentiment leans toward greed with Fear & Greed Index at 55.',

            // Economic Indicators
            'economic_indicators.inflation': 'Inflation (CPI) at 3.2% year-over-year, down from 3.4%, moderating toward Fed\'s 2% target.',
            'economic_indicators.fedFunds': 'Federal Reserve policy remained in focus this period; see the latest FOMC statement for current rate guidance.',
            'economic_indicators.gdp': 'US GDP growth at 2.3% in Q3, reflecting resilient consumer spending.',
            'economic_indicators.unemployment': 'Unemployment rate steady at 3.7% with 180,000 jobs added.',
            'economic_indicators.consumerConfidence': 'Consumer confidence rose to 102.5, suggesting continued optimism.',
            'economic_indicators.pmi': 'Manufacturing PMI at 48.7 (contraction), Services PMI at 54.3 (expansion).',
            'economic_indicators.retailSales': 'Retail sales increased 0.4%, driven by strong holiday shopping.',
            'economic_indicators.housingStarts': 'Housing starts declined 5% as elevated mortgage rates weigh on affordability.',
            'economic_indicators.tradeBalance': 'Trade deficit narrowed to $64.5B as exports grew faster than imports.',
            'economic_indicators.summary': 'Mixed economic picture with declining inflation (3.2%), steady unemployment (3.7%), resilient GDP growth (2.3%).',

            // Technical Analysis
            'technical_analysis.support.sp500': 'S&P 500 support: 6,500',
            'technical_analysis.resistance.sp500': 'S&P 500 resistance: 6,700',
            'technical_analysis.trend.shortTerm': 'Short-term trend neutral to slightly bullish.',
            'technical_analysis.trend.mediumTerm': 'Medium-term trend upward, above 50-day moving average.',
            'technical_analysis.trend.longTerm': 'Long-term trend firmly bullish, well above 200-day average.',
            'technical_analysis.movingAverages.ma50': 'S&P 500 trading 2.5% above 50-day MA at 6,450.',
            'technical_analysis.movingAverages.ma200': 'S&P 500 8.3% above 200-day MA at 6,100.',
            'technical_analysis.indicators.rsi': 'RSI at 58, neutral momentum with room for upside.',

            // Sector Analysis
            'sector_analysis.topGainers[0]': 'Technology: +0.82% - Led by semiconductors and software',
            'sector_analysis.topGainers[1]': 'Financials: +0.75% - Benefiting from higher rates',
            'sector_analysis.topGainers[2]': 'Healthcare: +0.65% - Defensive positioning',
            'sector_analysis.topLosers[0]': 'Energy: -1.2% - Falling oil prices',
            'sector_analysis.topLosers[1]': 'Materials: -0.8% - Weak manufacturing data',
            'sector_analysis.topLosers[2]': 'Utilities: -0.5% - Rising rates reduce appeal',
            'sector_analysis.themes': 'Technology leadership continues, defensive rotation into healthcare.',
            'sector_analysis.performance': '6 of 11 sectors advanced, led by Technology (+0.82%).',
            'sector_analysis.outlook': 'Expect continued technology outperformance, defensive positioning.',

            // Market News
            'news_items[0].headline': 'Markets monitor Federal Reserve policy signals',
            'news_items[1].headline': 'Technology sector shows resilience amid rate concerns',
            'news_items[2].headline': 'Consumer spending remains robust despite inflation',
            'news_items.summary': 'Top stories: Fed policy expectations, tech strength, consumer resilience.',
            'news_items.impact': 'News flow supporting cautious optimism.',
            'news_items.keyEvents': 'Watch: Fed FOMC meeting (Dec 13), Q4 earnings, November CPI (Dec 12).',

            // Investment Insights
            'investment_insights.opportunities': 'Opportunities in defensive sectors, quality dividend stocks, short-duration bonds.',
            'investment_insights.risks': 'Key risks: Persistent inflation, Fed uncertainty, geopolitical tensions.',
            'investment_insights.themes': 'AI and technology leadership, flight to quality, energy transition.',
            'investment_insights.assetAllocation': 'Balanced: 60% equities, 30% fixed income, 10% alternatives.',
            'investment_insights.recommendations': 'Focus on quality companies with strong balance sheets.',

            // Market Sentiment
            'market_sentiment.fearGreed.value': '55',
            'market_sentiment.fearGreed.level': 'Greed',
            'market_sentiment.fearGreed.description': 'Index at 55 indicates moderate greed, optimism but not euphoria.',
            'market_sentiment.vix.value': '18.45',
            'market_sentiment.vix.level': 'Low',
            'market_sentiment.putCallRatio': 'Put/Call ratio at 0.85 suggests neutral sentiment.',
            'market_sentiment.advanceDecline': 'Advance/Decline ratio positive at 1.5:1.',
            'market_sentiment.summary': 'Sentiment cautiously optimistic with Fear & Greed at 55, low VIX.',

            // Business Insights
            'business_insights.climate': 'Business climate cautiously optimistic with GDP at 2.3%, consumer confidence improving.',
            'business_insights.smallBusiness': 'Small business hiring steady, consumer spending supporting retail and services.',
            'business_insights.trends': 'AI adoption accelerating, supply chain regionalization creating opportunities.',
            'business_insights.opportunities': 'Energy transition, healthcare tech, reshoring, AI services in demand.',

            // Tax Reminders
            'tax_reminders.deadlines': 'December 31: RMDs, tax-loss harvesting. January 15: Q4 estimated taxes.',
            'tax_reminders.tips': 'Review tax-loss harvesting, Roth conversions, maximize retirement contributions.',
            'tax_reminders.documents': 'Gather: W-2/1099s, investment statements, mortgage interest, charitable receipts.',

            // Legacy aliases for backward compatibility
            'sp500_performance': '+0.13% (6,606.76)',
            'nasdaq_performance': '-0.07% (22,333.96)',
            'dow_performance': '-0.27% (45,757.90)',
            'treasury_yield': '4.18%',
            'market_summary': 'US markets posted mixed results with S&P 500 down -0.13%.',
            'portfolio_performance': 'Diversified portfolios continue to benefit from broad asset-class exposure; results vary by allocation and objectives — your advisor can review your specific performance with you.',
            'market_analysis': 'Markets showed healthy breadth with mid-caps outperforming.',
            'sector_highlights': '6 of 11 sectors advanced, Technology leading at +0.82%.',
            'economic_summary': 'Mixed economic picture with declining inflation, steady unemployment.',
            'investment_insights': 'Focus on quality, diversify across sectors, maintain cash reserves.',
            'sector_performance': '6 of 11 sectors advanced, led by Technology.',
            'week_ahead_outlook': 'Markets likely range-bound awaiting Fed clarity and earnings.',

            // Personal and human touch placeholders (new - industry best practice)
            'personal_note': 'I was at a client dinner last week and the most common question wasn\'t about the Fed or earnings - it was "should I be worried?" The short answer: no. The longer answer is in this week\'s update.',
            'human_touch': 'Currently reading "Same as Ever" by Morgan Housel - a wonderful reminder that while markets change, human behavior doesn\'t. If you enjoy thinking about the intersection of money and psychology, I highly recommend it.',

            // Generic communication placeholders
            'action_items': 'If you haven\'t reviewed your beneficiary designations in the past year, now is a great time. Reply to this email and we\'ll schedule a quick 15-minute check-in.',
            'meeting_cta': 'I\'d love to catch up. Reply to this email or grab a time that works for you: https://calendly.com/your-advisor',
            'compliance_disclaimer': 'This newsletter is for informational purposes only and should not be considered investment advice. Please consult with your financial advisor before making investment decisions. Past performance does not guarantee future results.',

            // ==== QUARTERLY STRATEGY TEMPLATE PLACEHOLDERS ====
            'quarter_number': Math.floor((new Date().getMonth() + 3) / 3),
            'year': new Date().getFullYear(),
            'quarterly_summary': 'Q4 2025 saw resilient market performance with the S&P 500 advancing 11.2% driven by robust earnings growth, declining inflation, and expectations of Federal Reserve rate cuts in early 2026.',
            'quarterly_market_performance': 'S&P 500: +11.2%, NASDAQ: +13.5%, Dow Jones: +9.8%, Russell 2000: +14.3%. International markets: MSCI EAFE +8.7%, Emerging Markets +10.2%.',
            'gdp_growth': '2.3% annual rate in Q3, reflecting resilient consumer spending and business investment.',
            'inflation_rate': 'CPI at 3.2% year-over-year, down from 3.4% last quarter, moderating toward Fed target.',
            'employment_data': 'Unemployment steady at 3.7% with 180,000 average monthly job gains. Labor market remains tight but showing signs of moderation.',
            'fed_policy_summary': 'Federal Reserve policy remains data-dependent; recent communications emphasize progress on inflation and the path of future rate adjustments.',
            'strategic_outlook': 'We maintain a constructive outlook for 2026, favoring quality growth stocks, short-duration bonds, and select international exposure. Key themes: AI productivity, energy transition, and demographic shifts.',
            'allocation_strategy': 'Recommended: 60% equities (55% US, 5% International), 35% fixed income (20% investment grade, 15% short-duration), 5% alternatives (commodities, real assets).',
            'investment_themes': 'AI and automation driving productivity. Energy transition creating opportunities in renewables and grid infrastructure. Reshoring benefiting US manufacturing. Aging demographics supporting healthcare innovation.',
            'portfolio_positioning': 'Current market positioning favors quality growth stocks, short-duration bonds, and selective international exposure. Technology and healthcare remain attractive sectors, while energy warrants caution.',
            'strategic_adjustments': 'Consider: 1) Adding to quality growth at reasonable valuations, 2) Extending duration selectively in fixed income, 3) Harvesting tax losses, 4) Rebalancing to target allocations.',
            'risk_assessment': 'Key risks: Inflation persistence, geopolitical tensions (Middle East, China-Taiwan), election uncertainty, recession probability ~25%. Mitigants: Diversification, quality focus, liquidity reserves.',
            'quarterly_action_items': 'Schedule portfolio review meeting to discuss rebalancing. Review tax-loss harvesting opportunities. Confirm beneficiaries and estate planning documents. Discuss any changes in financial goals or risk tolerance.',

            // ==== RETIREMENT FOCUS TEMPLATE PLACEHOLDERS ====
            'retirement_score': 'How prepared are you for retirement? The average American is on track to replace only 60-70% of their pre-retirement income. Let\'s make sure you\'re ahead of the curve.',
            'current_value': 'Contact your advisor for your current retirement account balances and progress toward your goals.',
            'retirement_date': 'Your target retirement date is an important anchor for planning. If you haven\'t reviewed yours recently, let\'s discuss whether your timeline still feels right.',
            'monthly_contributions': 'Are you maximizing your retirement contributions? Annual 401(k) and IRA limits are set by the IRS each year — confirm the current limits with your advisor and consider automating monthly contributions.',
            'projected_income': 'Retirement income typically comes from three sources: portfolio withdrawals (the 4% rule), Social Security, and any pensions. Let\'s make sure all three are optimized for your situation.',
            'recommendations': 'As you approach retirement, gradually shifting from growth to income and stability becomes important. A common guideline: subtract your age from 110 to find your target stock allocation.',
            'income_strategy': 'The 4% withdrawal rule suggests you can safely withdraw 4% of your portfolio annually in retirement. Combined with Social Security, this forms the foundation of a sustainable income plan.',
            'social_security_analysis': 'Did you know? Delaying Social Security from age 62 to 70 can increase your benefit by up to 77%. For many people, waiting is one of the best "guaranteed returns" available. Let\'s discuss what makes sense for you.',
            'retirement_tax_strategies': 'Roth conversion windows can save significant taxes in retirement. If your income is lower than usual this year, it may be an ideal time to convert some traditional IRA funds to a Roth - paying taxes now at a lower rate to enjoy tax-free withdrawals later.',
            'rmd_planning': 'Required Minimum Distributions begin at age 73. Strategic Roth conversions before that age can reduce the size of your future RMDs and the tax burden that comes with them.',
            'healthcare_planning': 'Medicare begins at 65, but it doesn\'t cover everything. Budget for premiums, supplemental insurance, and out-of-pocket costs - typically $6,000-$10,000 per year. HSA contributions now can help offset these costs later.',
            'retirement_action_items': 'One thing to do this month: review your retirement contribution amounts and make sure you\'re on track to max out this year. If you\'re over 50, don\'t forget the catch-up contribution. Reply to this email and I\'ll help you run the numbers.',

            // ==== HIGH NET WORTH TEMPLATE PLACEHOLDERS ====
            'wealth_summary': 'The wealth landscape continues to evolve. This month, I want to highlight three trends that matter most for high-net-worth families: shifting estate tax thresholds, new private market opportunities, and philanthropic strategies that create lasting impact.',
            'total_assets': 'Contact your advisor for your current total asset summary.',
            'liquid_assets': 'Contact your advisor for your current liquidity position.',
            'alternatives_value': 'Alternative investments continue to offer portfolio diversification benefits in the current environment.',
            'real_estate_value': 'Commercial real estate valuations are stabilizing after a period of adjustment, creating selective opportunities.',
            'private_market_opportunities': 'Current opportunities: Pre-IPO technology allocation ($500K minimum), healthcare venture fund (vintage 2025), commercial real estate development partnership. Expected IRR: 15-20% over 5-7 years.',
            'estate_planning_update': 'Current estate plan utilizes $27.36M lifetime exclusion (2025). Recommendation: Consider spousal lifetime access trust (SLAT) to lock in current exemption before potential 2026 reduction to $7M.',
            'advanced_tax_strategies': 'Implemented: Qualified small business stock exclusion, charitable remainder trust ($1.5M), family limited partnership. Opportunity: Installment sale to defective grantor trust to freeze estate value.',
            'philanthropic_planning': 'Current giving: $150,000 annually through donor-advised fund. Recommendation: Consider private foundation for greater control and family legacy. Explore program-related investments for impact.',
            'family_office_updates': 'Services active: Consolidated reporting, bill pay, property management, travel coordination, insurance review. New service: Family governance and next-generation wealth education program.',
            'exclusive_investments': 'Allocated to: Sequoia Capital Fund XXII, Blackstone Real Estate Partners X, Renaissance Institutional Equities Fund. Pending: SpaceX secondary opportunity, climate tech SPAC with Emerson Collective.',
            'team_contact': 'Your dedicated team: John Smith (Lead Advisor, 555-0100), Maria Rodriguez (Tax Strategist, 555-0101), David Chen (Estate Attorney, 555-0102). Available 24/7 for urgent matters.',

            // ==== MARKET ALERT TEMPLATE PLACEHOLDERS ====
            'alert_summary': 'MARKET ALERT: Significant market volatility observed today with S&P 500 declining -3.2% on concerns over unexpected inflation data and Federal Reserve commentary. VIX surged to 28.5, highest level in 3 months.',
            'market_event_description': 'Markets sold off sharply after CPI report showed inflation reaccelerating to 3.8% (vs. 3.2% expected), triggering concerns the Federal Reserve may delay rate cuts or potentially resume hiking.',
            'index_changes': 'S&P 500: -3.2% (6,395), NASDAQ: -4.1% (21,418), Dow Jones: -2.8% (44,479), Russell 2000: -3.7% (2,314). All 11 sectors declined, led by Technology (-4.8%) and Consumer Discretionary (-4.2%).',
            'vix_level': '28.5 (up from 18.4 yesterday), indicating elevated fear and uncertainty. Highest level since August market correction.',
            'safe_haven_performance': 'Treasury bonds rallied (10-year yield -12bps to 4.06%), Gold +1.8% to $2,485/oz, US Dollar +0.9%. Flight-to-quality behavior evident.',
            'portfolio_impact_analysis': 'Diversified portfolios typically declined 1.5-2.5% today, outperforming the broader market thanks to fixed income and defensive holdings providing downside protection. This is exactly why we build portfolios with balance.',
            'advisor_response': 'I\'m closely monitoring developments and remain confident in a long-term, diversified approach. Current volatility often creates buying opportunities in quality companies. My recommendation: stay disciplined and avoid making emotional decisions.',
            'immediate_recommendations': '1) Resist emotional selling - stay disciplined. 2) Consider tax-loss harvesting in taxable accounts. 3) Use cash reserves to rebalance if declines continue. 4) Maintain 6-12 month emergency fund.',
            'historical_context': 'Similar corrections occurred in August 2024 (-5.2%), October 2023 (-4.8%), and March 2023 (-3.9%). In each case, markets recovered within 3-6 weeks. Average pullback in bull markets: 4-5% every 3-4 months.',
            'reassurance_message': 'While today\'s decline is concerning, corrections are a normal part of investing. Well-diversified portfolios are built for moments like this. I remain focused on helping you achieve your long-term goals - not reacting to short-term noise.',
            'urgent_contact_info': 'We are here for you. Questions or concerns? Call your advisor directly at 555-0100 (mobile: 555-0199). Available evenings and weekends during market volatility.',

            // ==== TAX PLANNING TEMPLATE PLACEHOLDERS ====
            'tax_season_overview': 'Tax season preparation underway. Key deadlines approaching: January 15 (Q4 estimated taxes), April 15 (2025 tax returns), and December 31 (year-end planning strategies).',
            'tax_deadlines': 'December 31: Last day for tax-loss harvesting, RMDs, Roth conversions. January 15: Q4 2025 estimated tax payment. March 1: Business tax returns (S-Corp, Partnership). April 15: Individual tax returns. October 15: Extended returns.',
            'yearend_strategies': 'Opportunities: maximize retirement and HSA contributions up to current IRS limits, harvest tax losses, defer income, accelerate deductions, make charitable donations, consider Roth conversions, review estimated tax payments.',
            'tax_loss_opportunities': 'Review taxable accounts for unrealized losses that could be harvested to offset gains while maintaining market exposure through a substitution strategy. Your advisor can quantify the opportunity for your specific accounts.',
            // ('401k_limit' and 'ira_limit' are defined once above — duplicate
            // keys here previously carried CONTRADICTORY dollar figures, #367)
            'catchup_limits': 'Catch-up contributions are available from age 50 for 401(k)s and IRAs (and age 55 for HSAs); the IRS sets the amounts annually — confirm current figures before contributing.',
            'deduction_strategies': 'Consider: Bunching charitable donations (2 years in 1), maximizing mortgage interest deduction, state tax prepayment (check SALT cap), medical expenses (>7.5% AGI), business expense optimization.',
            'charitable_strategies': 'Strategies: Donate appreciated stock (avoid capital gains), qualified charitable distribution from IRA (age 70.5+), donor-advised fund for bunching, charitable remainder trust for large gifts.',
            'tax_efficient_investments': 'Place tax-inefficient assets (bonds, REITs, high-turnover funds) in tax-deferred accounts. Hold index funds, growth stocks in taxable accounts for lower tax drag and step-up basis at death.',
            'estate_tax_update': 'Federal estate and gift tax exemption levels are set annually and are scheduled to change under current law; state estate taxes vary widely. Review gifting strategies with your advisor and estate attorney.',
            'tax_action_items': 'Schedule tax planning meeting by December 1st. Review estimated tax payments. Maximize retirement contributions. Execute tax-loss harvesting. Gather charitable donation receipts. Update W-4 if needed.',
            'tax_meeting_cta': 'Schedule your year-end tax planning consultation: https://calendly.com/your-advisor/tax-planning. Bring: prior year returns, current income projections, major life changes.',

            // ==== CLIENT WELCOME TEMPLATE PLACEHOLDERS ====
            'welcome_message': 'Welcome to Premier Financial Advisors! We are thrilled to partner with you on your financial journey. Our team is committed to helping you achieve your goals through personalized planning and disciplined investment management.',
            'onboarding_overview': 'Your onboarding process includes: Discovery meeting (completed), investment policy statement development, portfolio implementation, account setup, beneficiary review, and goal tracking system configuration.',
            'client_goals_summary': 'Your primary financial goals: Retirement at age 65 with $100,000 annual income, College funding for 2 children ($200,000 total), Emergency fund 6 months expenses ($60,000), Home purchase in 5 years ($150,000 down payment).',
            'initial_portfolio': 'Recommended initial allocation: 70% stocks (45% US Large Cap, 15% US Small Cap, 10% International), 25% bonds (20% Investment Grade, 5% High Yield), 5% cash. Risk level: Moderate-Aggressive.',
            'investment_philosophy': 'Our approach: Long-term focus, diversification, tax efficiency, low costs, evidence-based strategies. We believe in discipline over speculation, patience over panic, and planning over predictions.',
            'update_frequency': 'Quarterly performance reports',
            'review_schedule': 'Annual comprehensive portfolio review (plus mid-year check-in)',
            'commentary_schedule': 'Monthly market commentary and insights',
            'tax_document_schedule': '1099s and consolidated tax statements by February 15th annually',
            'team_introduction': 'Your advisory team: John Smith, CFP® (Lead Advisor, 20 years experience), Sarah Johnson, CPA (Tax Strategist), Michael Chen, JD (Estate Planning), Emily Rodriguez, CFA (Portfolio Manager).',
            'communication_setup': 'Preferred contact: Email (weekly updates), Phone (quarterly reviews), Video calls (annual meetings). Client portal access: 24/7 account access, document vault, secure messaging.',
            'onboarding_checklist': '✅ Discovery meeting complete. ⏳ Account applications submitted. ⏳ Funding in process. Upcoming: Beneficiary designation, risk assessment questionnaire, financial plan delivery (2 weeks).',
            'portal_link': 'https://client.premierfinancial.com (credentials sent separately)',
            'app_link': 'Download "Premier Financial" from App Store or Google Play',
            'support_contact': 'Client Service Team: 1-800-555-0199, support@premierfinancial.com, Live chat 9AM-6PM ET M-F',
            'welcome_support': 'Questions about your account setup, documents, or next steps? Contact your advisor John Smith at 555-0100 or john.smith@premierfinancial.com. We typically respond within 4 business hours.',

            // ==== LIFE EVENT TEMPLATE PLACEHOLDERS ====
            'life_event_actions': '1. **Update your beneficiaries** — life changes mean your designations may need refreshing.\n2. **Review your insurance coverage** — make sure your protection matches your new situation.\n3. **Revisit your emergency fund** — your needs may have changed. Let\'s make sure you have the right cushion.',
            'life_event_planning': 'Every major life event is an opportunity to strengthen your financial foundation. Whether it\'s adjusting your savings rate, updating your estate plan, or rethinking your timeline, I\'m here to help you navigate it. The best time to plan is right now.',

            // ==== EDUCATIONAL DEEP-DIVE TEMPLATE PLACEHOLDERS ====
            'education_topic': 'Roth Conversions: Is This the Right Year?',
            'education_intro': 'I\'ve been getting a lot of questions about Roth conversions lately, and with good reason. With tax rates potentially changing and markets at interesting levels, this could be a smart move for many of you. Let me break it down.',
            'education_explanation': 'A Roth conversion moves money from a traditional IRA (pre-tax) to a Roth IRA (after-tax). You pay taxes now, but the money grows tax-free forever. Think of it as paying the tax bill today to avoid a potentially bigger one in retirement.',
            'education_application': 'This strategy works especially well if you\'re in a lower tax bracket than usual (between jobs, early retirement, gap year), if you expect tax rates to rise, or if you want to leave tax-free money to your heirs.',
            'education_mistakes': '**Mistake #1:** Converting too much in one year and pushing yourself into a higher bracket. **Mistake #2:** Using IRA money to pay the tax bill (use outside funds instead). **Mistake #3:** Not considering the impact on Medicare premiums (IRMAA).',
            'education_summary': 'Roth conversions aren\'t right for everyone, but for the right person at the right time, they can save tens of thousands in lifetime taxes. If you\'re curious whether this makes sense for you, let\'s run the numbers together.',

            // ==== YEAR-END REVIEW TEMPLATE PLACEHOLDERS ====
            'yearly_market_summary': 'What a year it\'s been. Markets delivered strong returns despite the headlines trying to convince us otherwise. The S&P 500 advanced meaningfully, driven by a broadening rally that finally extended beyond the mega-cap tech names that dominated the first half.',
            'yearly_highlights': '1. **Inflation came down** — from worrying levels to something much more manageable. The Fed\'s patience paid off.\n2. **Jobs stayed strong** — the much-predicted recession never materialized. Consumers kept spending.\n3. **AI went mainstream** — not just in stock prices, but in actual business productivity. This theme has staying power.',
            'yearly_lessons': 'The biggest lesson? Staying invested beats timing the market every single time. Clients who stayed the course through the spring volatility were rewarded handsomely by year-end. The ones who waited for the "right time" to get back in are still waiting.',
            'yearly_outlook': 'I\'m cautiously optimistic about the year ahead. Valuations are stretched in some areas, but earnings growth is accelerating and the interest rate environment is becoming more favorable. I expect more normal returns — think high single digits rather than another blockbuster year.',
            'yearly_watchlist': 'Three things I\'m watching closely: (1) How quickly the Fed cuts rates — too fast signals trouble, too slow signals stubbornness. (2) Whether earnings growth can keep up with expectations. (3) The election cycle and its impact on investor sentiment.',
            'yearly_action_items': 'Before we turn the page, here are three things to address: Review your asset allocation — has drift taken you off target? Max out your retirement contributions before the deadline. And let\'s schedule your annual review to make sure your plan reflects your current goals.',
            'next_year': `${new Date().getFullYear() + 1}`,

            // ==== REFERRAL & RE-ENGAGEMENT TEMPLATE PLACEHOLDERS ====
            'reengagement_note': 'I realized it\'s been a while since we last connected, and I wanted to reach out. A lot has happened in the markets and the economy, and I want to make sure your plan still reflects where you are today. Sometimes a quick check-in can make a big difference.'
        };

        // Include the 27 non-client-specific placeholders
        const nonClientPlaceholders = this.getNonClientPlaceholders();

        // Add production safety flags - CRITICAL for preventing mock data in production
        return {
            ...placeholders,
            ...nonClientPlaceholders, // Add the 27 non-client placeholders
            // Metadata flags
            _isFallback: true,
            _fallbackType: 'hardcoded',
            _isProductionReady: false,
            _fallbackReason: 'Using synchronous getAllPlaceholders() method - returns hardcoded example data',
            _warningMessage: 'You are viewing example/mock data. Real-time market data is not available. Do not send newsletters with this data.',
            // Timestamp for debugging
            _generatedAt: new Date().toISOString()
        };
    }

    /**
     * Fill template with pre-generated placeholder values
     * This is the COST-OPTIMIZED approach: Fill content BEFORE sending to LLM
     * LLM only formats and adjusts tone/style (cheap operation)
     *
     * @param {string} templateContent - Template string with {{placeholder}} syntax
     * @param {Object} additionalData - Additional client-specific data (optional)
     * @returns {string} Template with ALL placeholders replaced with actual content
     */
    /**
     * #367 — single implementation of the banned-portfolio-placeholder
     * check shared by fillTemplate and fillTemplateAsync (review dedup:
     * two identical blocks were guaranteed to drift).
     * @private
     */
    _stripBannedPlaceholder(trimmedKey) {
        if (!BANNED_PORTFOLIO_PLACEHOLDERS.has(trimmedKey)) return false;
        this.logger.warn(`Stripped banned portfolio placeholder: {{${trimmedKey}}}`);
        return true;
    }

    fillTemplate(templateContent, additionalData = {}) {
        if (!templateContent) {
            this.logger.warn('No template content provided to fillTemplate');
            return '';
        }

        this.logger.info('Filling template with pre-generated content...');

        // Get all 72 pre-generated placeholders
        const placeholders = this.getAllPlaceholders();

        // Merge with additional client-specific data
        const allData = {
            ...placeholders,
            ...additionalData
        };

        let filledContent = templateContent;
        let replacementCount = 0;

        // Replace all {{placeholder}} patterns
        filledContent = templateContent.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
            const trimmedKey = key.trim();

            // #367: banned portfolio placeholders are stripped outright —
            // MVP has no portfolio data, so any value here is fabricated.
            if (this._stripBannedPlaceholder(trimmedKey)) {
                return '';
            }

            // Check if we have a value for this placeholder
            if (Object.prototype.hasOwnProperty.call(allData, trimmedKey)) {
                replacementCount++;
                return allData[trimmedKey];
            }

            // If not found, log warning and keep placeholder
            this.logger.warn(`No value found for placeholder: {{${trimmedKey}}}`);
            return match; // Keep original placeholder if no value found
        });

        this.logger.info(`✅ Filled ${replacementCount} placeholders in template`);
        this.logger.info(`Template size: ${templateContent.length} → ${filledContent.length} characters`);

        return filledContent;
    }

    /**
     * Async version of fillTemplate that resolves placeholders from Custom Content Library
     * before falling back to static defaults. Use this for production newsletter generation.
     *
     * Resolution order:
     *   1. additionalData (client-specific overrides)
     *   2. Custom Content Library (advisor-authored: personal_note, human_touch)
     *   3. S3/Lambda AI-generated placeholders (market data)
     *   4. Static fallback placeholders
     *
     * @param {string} templateContent - Template string with {{placeholder}} syntax
     * @param {Object} additionalData - Additional client-specific data (optional)
     * @returns {Promise<string>} Template with ALL placeholders replaced
     */
    async fillTemplateAsync(templateContent, additionalData = {}) {
        if (!templateContent) {
            this.logger.warn('No template content provided to fillTemplateAsync');
            return '';
        }

        this.logger.info('Filling template with Custom Content Library + pre-generated content...');

        // Step 1: Get static/AI-generated placeholders (72 pre-generated)
        const placeholders = this.getAllPlaceholders();

        // Step 2: Get Custom Content Library placeholders (personal_note, human_touch)
        let customContentPlaceholders = {};
        try {
            customContentPlaceholders = await customContentService.getPlaceholderContent();
            if (Object.keys(customContentPlaceholders).length > 0) {
                this.logger.info(`Resolved ${Object.keys(customContentPlaceholders).length} placeholders from Custom Content Library: ${Object.keys(customContentPlaceholders).join(', ')}`);
            }
        } catch (error) {
            this.logger.warn('Failed to fetch Custom Content Library placeholders, using static fallbacks:', error);
        }

        // Step 3: Merge in priority order (later entries override earlier)
        const allData = {
            ...placeholders,              // Static/AI fallbacks (lowest priority)
            ...customContentPlaceholders, // Custom Content Library (medium priority)
            ...additionalData             // Client-specific overrides (highest priority)
        };

        let filledContent = templateContent;
        let replacementCount = 0;
        let customContentCount = 0;

        // Replace all {{placeholder}} patterns
        filledContent = templateContent.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
            const trimmedKey = key.trim();

            // #367: banned portfolio placeholders are stripped outright —
            // MVP has no portfolio data, so any value here is fabricated.
            if (this._stripBannedPlaceholder(trimmedKey)) {
                return '';
            }

            if (Object.prototype.hasOwnProperty.call(allData, trimmedKey)) {
                replacementCount++;
                if (Object.prototype.hasOwnProperty.call(customContentPlaceholders, trimmedKey)) {
                    customContentCount++;
                }
                return allData[trimmedKey];
            }

            this.logger.warn(`No value found for placeholder: {{${trimmedKey}}}`);
            return match;
        });

        this.logger.info(`Filled ${replacementCount} placeholders (${customContentCount} from Custom Content Library)`);

        return filledContent;
    }

    /**
     * Get batch placeholders (async) - fetches from backend
     * For production use when real-time data is needed
     * @returns {Promise<Object>} Batch placeholder data from Lambda
     */
    async getBatchPlaceholders() {
        const data = await this.getCurrentPlaceholders();
        return data.placeholders || this.getAllPlaceholders();
    }

    /**
     * Get client-specific placeholders (merged from ClientSpecificPlaceholderService)
     * MVP Note: Returns fallback data since portfolio integration not available in MVP
     * @param {string} clientId - Client identifier
     * @param {Object} context - Context for generation (unused in MVP)
     * @returns {Promise<Object>} Client-specific placeholder content
     */
    async getClientSpecificPlaceholders(clientId, context = {}) {
        // In MVP, return fallback content since portfolio data integration not available
        // See CLAUDE.md: "Client-specific portfolio placeholders are NOT supported in MVP"
        return {
            success: true,
            clientId,
            content: this._getClientFallbackContent(),
            metadata: {
                generatedAt: new Date().toISOString(),
                source: 'fallback',
                note: 'MVP does not support client portfolio data integration'
            }
        };
    }

    /**
     * Get fallback content for client-specific placeholders (MVP)
     * @private
     * @returns {Object} Fallback placeholder values
     */
    _getClientFallbackContent() {
        return {
            'portfolio_summary.performance': 'Your portfolio performance is being calculated.',
            'portfolio_summary.allocation': 'Asset allocation analysis in progress.',
            'portfolio_summary.insights': 'Portfolio insights will be available shortly.',
            'portfolio_summary.risk_metrics': 'Risk metrics are being updated.',
            'investment_insights.personalizedRecommendations': 'Personalized recommendations coming soon.',
            // 'investment_insights.rebalancing_suggestions' removed (#367) —
            // banned portfolio-family key; fillTemplate hard-strips it.
            'investment_insights.opportunity_alerts': 'Scanning for new opportunities.',
            'action_items.personalizedTasks': 'Your action items are being prepared.',
            'action_items.deadlines': 'Important deadlines will appear here.',
            'action_items.priority_actions': 'Priority actions being identified.',
            'tax_considerations.current_year': 'Tax analysis in progress.',
            'tax_considerations.harvesting_opportunities': 'Tax harvesting opportunities being evaluated.',
            'goal_progress.status': 'Goal progress tracking active.',
            'alerts.portfolio_specific': 'Portfolio alerts will appear here.',
            'performance.vs_benchmark': 'Benchmark comparison being calculated.'
        };
    }
}

// Export singleton instance
const lambdaPlaceholderService = new LambdaPlaceholderService();
export default lambdaPlaceholderService;

// Also export the class for testing
export { LambdaPlaceholderService };